import React, { useState, useEffect, useCallback } from 'react';
import {
  getSwingSettings, updateSwingSettings, runSwingScan,
  getSwingScanStatus, getSwingScanResults, getSwingPositions, getSwingAlerts,
} from '../../api/client';

export default function SwingDashboard({ onNavigate }) {
  const [settings, setSettings]       = useState(null);
  const [capital, setCapital]         = useState('');
  const [saving, setSaving]           = useState(false);
  const [scanning, setScanning]       = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [positions, setPositions]     = useState([]);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [showAllSectors, setShowAllSectors] = useState(false);   // sector heatmap toggle
  const SECTOR_DEFAULT_VISIBLE = 10;

  const load = useCallback(async () => {
    try {
      const [s, pos, al] = await Promise.all([
        getSwingSettings(),
        getSwingPositions({ status: 'ACTIVE' }),
        getSwingAlerts({ acknowledged: false }),
      ]);
      setSettings(s.data);
      setCapital(s.data.capital?.toString() || '');
      setPositions(pos.data.positions || []);
      setUnreadAlerts(al.data.unread || 0);

      const sr = await getSwingScanResults({ limit: 1000 });
      setScanResults(sr.data.results || []);
    } catch {
      setError('Failed to load dashboard data');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveCapital = async () => {
    const c = parseFloat(capital);
    if (isNaN(c) || c < 10000) { setError('Capital must be ≥ ₹10,000'); return; }
    setSaving(true);
    try {
      await updateSwingSettings({ capital: c });
      setSuccess('Capital saved');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleScan = async () => {
    setScanning(true);
    setError('');
    try {
      await runSwingScan();
      const poll = setInterval(async () => {
        try {
          const st = await getSwingScanStatus();
          if (!st.data.running) {
            clearInterval(poll);
            const sr = await getSwingScanResults({ limit: 1000 });
            setScanResults(sr.data.results || []);
            setScanning(false);
            setSuccess('Scan complete!');
            setTimeout(() => setSuccess(''), 4000);
          }
        } catch {
          clearInterval(poll);
          setScanning(false);
        }
      }, 5000);
    } catch { setError('Failed to start scan'); setScanning(false); }
  };

  const totalDeployed = positions.reduce((s, p) => s + (p.entry_price || 0) * (p.quantity || 0), 0);
  const cap          = settings?.capital || 0;
  const cashReserve  = cap - totalDeployed;

  const tile = (label, value, sub, color = '#fff') => (
    <div className="card" style={{ minWidth: 140, textAlign: 'center', flex: '1 1 140px' }}>
      <div style={{ color: '#888', fontSize: 12, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ color: '#666', fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      {error && (
        <div style={{ color: '#f44', marginBottom: 12, padding: '8px 12px', background: '#2a1010', borderRadius: 6 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ color: '#4CAF50', marginBottom: 12, padding: '8px 12px', background: '#0a2010', borderRadius: 6 }}>
          {success}
        </div>
      )}

      {/* Capital input */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Investment Capital</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Capital (₹)</label>
            <input
              type="number"
              value={capital}
              onChange={e => setCapital(e.target.value)}
              placeholder="e.g. 500000"
              style={{ padding: '8px 12px', background: '#1e1e1e', border: '1px solid #444', color: '#fff', borderRadius: 6, width: 180 }}
            />
          </div>
          <button
            onClick={handleSaveCapital}
            disabled={saving}
            style={{ padding: '8px 18px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={handleScan}
            disabled={scanning}
            style={{ padding: '8px 18px', background: scanning ? '#555' : '#2196F3', color: '#fff', border: 'none', borderRadius: 6, cursor: scanning ? 'not-allowed' : 'pointer' }}
          >
            {scanning ? '⏳ Scanning...' : '🔍 Run Scan Now'}
          </button>
          {scanning && (
            <span style={{ color: '#888', fontSize: 12 }}>
              Full Nifty 500 scan running — may take 3–10 minutes
            </span>
          )}
        </div>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        {tile('Total Capital', `₹${cap.toLocaleString('en-IN')}`, null, '#fff')}
        {tile('Deployed', `₹${Math.round(totalDeployed).toLocaleString('en-IN')}`, `${cap ? ((totalDeployed / cap) * 100).toFixed(1) : 0}% of capital`, '#2196F3')}
        {tile('Cash Reserve', `₹${Math.round(cashReserve).toLocaleString('en-IN')}`, 'available to deploy', '#4CAF50')}
        {tile('Open Positions', positions.length, settings ? `target: ${settings.n_positions}` : null, '#FF9800')}
        {tile('Unread Alerts', unreadAlerts, null, unreadAlerts > 0 ? '#f44336' : '#888')}
        {tile('Scan Results', scanResults.length > 0 ? 'Ready' : 'None', 'run scan to populate', scanResults.length > 0 ? '#4CAF50' : '#555')}
      </div>

      {/* Quick links */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
        <button onClick={() => onNavigate('scanner')} style={{ padding: '10px 20px', background: '#1e1e1e', border: '1px solid #444', color: '#4CAF50', borderRadius: 6, cursor: 'pointer' }}>
          🔍 View Scanner Results →
        </button>
        <button onClick={() => onNavigate('portfolio')} style={{ padding: '10px 20px', background: '#1e1e1e', border: '1px solid #444', color: '#2196F3', borderRadius: 6, cursor: 'pointer' }}>
          💼 My Portfolio →
        </button>
        <button onClick={() => onNavigate('journal')} style={{ padding: '10px 20px', background: '#1e1e1e', border: '1px solid #444', color: '#FF9800', borderRadius: 6, cursor: 'pointer' }}>
          📒 Trade Journal →
        </button>
        {unreadAlerts > 0 && (
          <button onClick={() => onNavigate('alerts')} style={{ padding: '10px 20px', background: '#2a1010', border: '1px solid #f44', color: '#f44', borderRadius: 6, cursor: 'pointer' }}>
            🔔 {unreadAlerts} Unread Alerts →
          </button>
        )}
      </div>

      {/* Sector Heatmap — scanner hits by sector */}
      {scanResults.length > 0 && (() => {
        // Aggregate from scan results loaded in state
        const sectorMap = {};
        scanResults.forEach(r => {
          const s = r.sector || 'Other';
          if (!sectorMap[s]) sectorMap[s] = { count: 0, totalScore: 0 };
          sectorMap[s].count++;
          sectorMap[s].totalScore += r.composite_score || 0;
        });
        const allSectors = Object.entries(sectorMap)
          .map(([name, d]) => ({ name, count: d.count, avgScore: Math.round(d.totalScore / d.count) }))
          .sort((a, b) => b.count - a.count);
        const sectors = showAllSectors ? allSectors : allSectors.slice(0, SECTOR_DEFAULT_VISIBLE);
        const maxCount = Math.max(...allSectors.map(s => s.count));   // scale to full set so bars stay comparable after toggle
        const hiddenCount = allSectors.length - SECTOR_DEFAULT_VISIBLE;
        return (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                📊 Sector Heatmap
                <span style={{ fontSize: 11, color: '#666', marginLeft: 8 }}>
                  scanner hits by sector
                  {' · '}
                  {showAllSectors
                    ? `${allSectors.length} sectors`
                    : `Top ${Math.min(SECTOR_DEFAULT_VISIBLE, allSectors.length)} of ${allSectors.length} sectors`}
                </span>
              </div>
              {hiddenCount > 0 && (
                <button
                  onClick={() => setShowAllSectors(v => !v)}
                  style={{
                    background: 'none', border: '1px solid #444', color: '#4CAF50',
                    padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                    fontSize: 11, fontWeight: 600,
                  }}
                >
                  {showAllSectors ? `Show top ${SECTOR_DEFAULT_VISIBLE}` : `Show all ${allSectors.length}`}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sectors.map(s => (
                <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 140, fontSize: 12, color: '#aaa', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.name}>
                    {s.name}
                  </div>
                  <div style={{ flex: 1, background: '#1e1e1e', borderRadius: 4, height: 18, overflow: 'hidden', position: 'relative' }}>
                    <div style={{
                      width: `${(s.count / maxCount) * 100}%`,
                      height: '100%',
                      background: s.avgScore >= 70 ? '#2E7D32' : s.avgScore >= 50 ? '#1565C0' : '#4a3500',
                      borderRadius: 4,
                      transition: 'width 0.4s',
                    }} />
                    <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#ccc' }}>
                      {s.count} stock{s.count !== 1 ? 's' : ''} · avg {s.avgScore}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 10, color: '#666' }}>
              <span>■ <span style={{ color: '#2E7D32' }}>High score (≥70)</span></span>
              <span>■ <span style={{ color: '#1565C0' }}>Medium (≥50)</span></span>
              <span>■ <span style={{ color: '#4a3500' }}>Lower (&lt;50)</span></span>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
